import { z } from 'zod';
import { SdkModuleCodeSchema, SdkReleaseChannelSchema } from './module-blueprint.js';

const IdSchema = z.string().min(1).max(160);
const RefSchema = z.string().min(1).max(240);
const VersionSchema = z.string().min(1).max(80);

export const SdkFoundationLayerSchema = z.enum(['core', 'horizontal', 'vertical-base']);
export type SdkFoundationLayer = z.infer<typeof SdkFoundationLayerSchema>;

export const SdkFoundationSourceSchema = z.enum(['boox-network', 'app-boox', 'bundled-baseline', 'local-sandbox']);
export type SdkFoundationSource = z.infer<typeof SdkFoundationSourceSchema>;

export const SdkFoundationScopeSchema = z.enum(['platform', 'market', 'tenant', 'sandbox']);
export type SdkFoundationScope = z.infer<typeof SdkFoundationScopeSchema>;

export const SdkMarketCodeSchema = z.enum(['global', 'sg', 'my', 'id']);
export type SdkMarketCode = z.infer<typeof SdkMarketCodeSchema>;

export const SdkFoundationVersionPolicySchema = z.object({
  currentVersion: VersionSchema,
  minimumCompatibleVersion: VersionSchema,
  channel: SdkReleaseChannelSchema,
  latestLookup: z.object({
    source: SdkFoundationSourceSchema,
    endpointRef: RefSchema,
    cacheTtlSeconds: z.number().int().positive().default(300),
  }),
  pinningRequired: z.boolean().default(true),
  allowImplicitLatest: z.literal(false).default(false),
});
export type SdkFoundationVersionPolicy = z.infer<typeof SdkFoundationVersionPolicySchema>;

export const SdkFoundationFailoverPolicySchema = z.object({
  primarySource: SdkFoundationSourceSchema,
  fallbackOrder: z.array(SdkFoundationSourceSchema).min(1),
  strategy: z.enum(['last-known-good', 'bundled-baseline', 'blocked']),
  requireSignature: z.boolean().default(true),
  allowLatestFallback: z.literal(false).default(false),
});
export type SdkFoundationFailoverPolicy = z.infer<typeof SdkFoundationFailoverPolicySchema>;

export const SdkSandboxCapabilitySchema = z.object({
  sandboxId: IdSchema,
  label: z.string().min(1).max(160),
  scope: SdkFoundationScopeSchema.default('sandbox'),
  dataMode: z.enum(['synthetic-only', 'masked-tenant-copy', 'empty-seed']).default('synthetic-only'),
  outboundSideEffects: z.enum(['blocked', 'approval-only']).default('blocked'),
  promotionGateRefs: z.array(RefSchema).min(1),
  resetPolicy: z.enum(['on-demand', 'per-session', 'daily']).default('on-demand'),
});
export type SdkSandboxCapability = z.infer<typeof SdkSandboxCapabilitySchema>;

export const SdkCoreCapabilitySchema = z.enum([
  'schema',
  'validator',
  'registry',
  'version',
  'failover',
  'sandbox',
]);
export type SdkCoreCapability = z.infer<typeof SdkCoreCapabilitySchema>;

export const SdkCoreFoundationSchema = z.object({
  layer: z.literal('core').default('core'),
  capabilityRefs: z.array(SdkCoreCapabilitySchema).min(6),
  schemaRegistryRef: RefSchema,
  validatorRef: RefSchema,
  moduleRegistryRef: RefSchema,
  versionPolicy: SdkFoundationVersionPolicySchema,
  failoverPolicy: SdkFoundationFailoverPolicySchema,
  sandbox: SdkSandboxCapabilitySchema,
}).superRefine((core, ctx) => {
  const required: SdkCoreCapability[] = ['schema', 'validator', 'registry', 'version', 'failover', 'sandbox'];
  const missing = required.filter((capability) => !core.capabilityRefs.includes(capability));
  if (missing.length > 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['capabilityRefs'],
      message: `Core foundation is missing required capabilities: ${missing.join(', ')}`,
    });
  }
});
export type SdkCoreFoundation = z.infer<typeof SdkCoreFoundationSchema>;

export const SdkHorizontalCapabilityKindSchema = z.enum(['task', 'document', 'report', 'relation', 'ai-adapter', 'work-surface']);
export type SdkHorizontalCapabilityKind = z.infer<typeof SdkHorizontalCapabilityKindSchema>;

export const SdkHorizontalCapabilitySchema = z.object({
  capabilityId: IdSchema,
  kind: SdkHorizontalCapabilityKindSchema,
  label: z.string().min(1).max(160),
  contractRef: RefSchema,
  profileRef: RefSchema,
  reusableByModuleCodes: z.array(SdkModuleCodeSchema).min(1),
  dataAccessRefs: z.array(RefSchema).default([]),
  uiSlotRefs: z.array(RefSchema).default([]),
  aiToolRefs: z.array(RefSchema).default([]),
  requiresAssignment: z.boolean().default(false),
  requiresAudit: z.boolean().default(true),
});
export type SdkHorizontalCapability = z.infer<typeof SdkHorizontalCapabilitySchema>;

export const SdkHorizontalFoundationSchema = z.object({
  layer: z.literal('horizontal').default('horizontal'),
  capabilities: z.array(SdkHorizontalCapabilitySchema).min(5),
}).superRefine((foundation, ctx) => {
  const required: SdkHorizontalCapabilityKind[] = ['task', 'document', 'report', 'relation', 'ai-adapter'];
  const present = new Set(foundation.capabilities.map((capability) => capability.kind));
  const missing = required.filter((kind) => !present.has(kind));
  if (missing.length > 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['capabilities'],
      message: `Horizontal foundation is missing reusable capability kinds: ${missing.join(', ')}`,
    });
  }
});
export type SdkHorizontalFoundation = z.infer<typeof SdkHorizontalFoundationSchema>;

export const SdkPlannerBaseSchema = z.object({
  baseId: IdSchema,
  rendererRef: RefSchema,
  supportsHorizontalSplit: z.boolean().default(true),
  supportsVerticalSplit: z.boolean().default(true),
  splitPolicy: z.object({
    horizontalRightPanelOptions: z.array(z.string().min(1)).default([]),
    verticalLeftBottomOptions: z.array(z.string().min(1)).default([]),
    filtersPerPanel: z.boolean().default(true),
  }),
  listContractRefs: z.array(RefSchema).min(1),
});
export type SdkPlannerBase = z.infer<typeof SdkPlannerBaseSchema>;

export const SdkJobWorkspaceBaseSchema = z.object({
  baseId: IdSchema,
  workspaceRef: RefSchema,
  masterEntityRef: RefSchema,
  detailEntityRefs: z.array(RefSchema).default([]),
  rightDockDetail: z.boolean().default(true),
  lockedByDefault: z.boolean().default(true),
});
export type SdkJobWorkspaceBase = z.infer<typeof SdkJobWorkspaceBaseSchema>;

export const SdkDocumentControlBaseSchema = z.object({
  baseId: IdSchema,
  documentProfileRef: RefSchema,
  attachmentCapabilityRef: RefSchema,
  approvalRefs: z.array(RefSchema).default([]),
  versioned: z.boolean().default(true),
});
export type SdkDocumentControlBase = z.infer<typeof SdkDocumentControlBaseSchema>;

export const SdkVerticalBaseProfileSchema = z.object({
  moduleCode: SdkModuleCodeSchema,
  label: z.string().min(1).max(160),
  marketOverlays: z.array(SdkMarketCodeSchema).default(['global']),
  plannerBase: SdkPlannerBaseSchema,
  jobWorkspaceBase: SdkJobWorkspaceBaseSchema,
  documentControlBase: SdkDocumentControlBaseSchema,
  horizontalCapabilityRefs: z.array(RefSchema).min(1),
  extensionSlots: z.array(RefSchema).default([]),
});
export type SdkVerticalBaseProfile = z.infer<typeof SdkVerticalBaseProfileSchema>;

export const SdkVerticalBaseFoundationSchema = z.object({
  layer: z.literal('vertical-base').default('vertical-base'),
  activeVerticalModuleCode: SdkModuleCodeSchema.default('TM'),
  profiles: z.array(SdkVerticalBaseProfileSchema).min(1),
}).superRefine((foundation, ctx) => {
  const required = ['TM', 'TP', 'CT', 'WM', 'CF', 'FM', 'SC', 'MT', 'HRM'];
  const present = new Set(foundation.profiles.map((profile) => profile.moduleCode));
  const missing = required.filter((moduleCode) => !present.has(moduleCode));
  if (missing.length > 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['profiles'],
      message: `Vertical base foundation is missing module profiles: ${missing.join(', ')}`,
    });
  }
  if (!present.has(foundation.activeVerticalModuleCode)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['activeVerticalModuleCode'],
      message: `activeVerticalModuleCode ${foundation.activeVerticalModuleCode} must exist in vertical base profiles.`,
    });
  }
});
export type SdkVerticalBaseFoundation = z.infer<typeof SdkVerticalBaseFoundationSchema>;

export const SdkFoundationManifestSchema = z.object({
  manifestId: IdSchema,
  name: z.string().min(1).max(160),
  version: VersionSchema,
  description: z.string().max(1000).optional(),
  source: SdkFoundationSourceSchema,
  markets: z.array(SdkMarketCodeSchema).min(1),
  core: SdkCoreFoundationSchema,
  horizontal: SdkHorizontalFoundationSchema,
  verticalBase: SdkVerticalBaseFoundationSchema,
});
export type SdkFoundationManifest = z.infer<typeof SdkFoundationManifestSchema>;

export function parseSdkFoundationManifest(input: unknown): SdkFoundationManifest {
  return SdkFoundationManifestSchema.parse(input);
}

export function validateSdkFoundationManifest(input: unknown): {
  ok: true;
  manifest: SdkFoundationManifest;
} | {
  ok: false;
  issues: string[];
} {
  const parsed = SdkFoundationManifestSchema.safeParse(input);
  if (parsed.success) return { ok: true, manifest: parsed.data };
  return {
    ok: false,
    issues: parsed.error.issues.map((issue) => `${issue.path.join('.') || '<root>'}: ${issue.message}`),
  };
}
